import { useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Image,
  Platform,
  Share,
  Alert,
  Modal,
  Pressable,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useFocusEffect, useRouter, useLocalSearchParams } from "expo-router";
import { getDog, getDogStats, getRuns, getRankings, deleteRun as apiDeleteRun, updateDog, getDogFollowers, type FollowerEntry } from "../../lib/api";
import { computeYearStats } from "../../lib/calculations";
import * as ImagePicker from "expo-image-picker";
import { StatsCard } from "../../../components/StatsCard";
import { DogTradingCard } from "../../../components/DogTradingCard";
import { TitleProgress } from "../../../components/TitleProgress";
import { HistoricalRankings } from "../../../components/HistoricalRankings";
import { DashFact } from "../../../components/DashFact";
import { DashInsight } from "../../../components/DashInsight";
import { RunCard } from "../../../components/RunCard";
import { SpeedChart } from "../../../components/SpeedChart";
import { BreedStandings } from "../../../components/BreedStandings";
import { TitlesEarned } from "../../../components/TitlesEarned";
import { Ionicons } from "@expo/vector-icons";
import { hasSizeSplit, heightToSizeClass } from "../../lib/sizeClass";
import { findAkcMatch } from "../../lib/akc-match";
import type { Dog, DogStats, Run, BreedRanking } from "../../lib/types";

export default function DogProfileScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [dog, setDog] = useState<Dog | null>(null);
  const [stats, setStats] = useState<DogStats | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [rankings, setRankings] = useState<BreedRanking[]>([]);
  const [hypotheticalRank, setHypotheticalRank] = useState<{ rank: number; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [runYearFilter, setRunYearFilter] = useState<string>(new Date().getFullYear().toString());
  const [runYearPickerOpen, setRunYearPickerOpen] = useState(false);
  const [followers, setFollowers] = useState<FollowerEntry[] | null>(null);
  const [runSort, setRunSort] = useState<"newest" | "fastest" | "slowest">("newest");

  async function fetchAll() {
    if (!id) return;
    try {
      const [dogData, statsData, runsData] = await Promise.all([
        getDog(id),
        getDogStats(id),
        getRuns(id),
      ]);
      setDog(dogData);
      setStats(statsData);
      setRuns(runsData);
      setError("");

      // ALWAYS fetch breed rankings — they're needed for the OFFICIAL AKC rank
      // badge (via findAkcMatch), independent of whether the dog has current-
      // year runs. Only the PROJECTION computation is gated on bestThreeAvgSpeed.
      // For variety breeds, scope to the dog's size class bucket.
      if (dogData.breed) {
        try {
          const currentYear = new Date().getFullYear().toString();
          const dogSizeClass = hasSizeSplit(dogData.breed)
            ? heightToSizeClass(dogData.heightInches) ?? "12_to_18"
            : undefined;
          const breedRankings = await getRankings(dogData.breed, currentYear, dogSizeClass);
          setRankings(breedRankings);

          // Projection only fires when dog has 3+ current-year runs.
          // bestThreeAvgSpeed is current-year only (services/runs.ts).
          if (breedRankings.length > 0 && statsData.bestThreeAvgSpeed > 0) {
            const rankingSpeed = statsData.bestThreeAvgSpeed;
            // Exclude the dog's own AKC entry so a top-ranked dog doesn't
            // compete with itself (Sienna at AKC #1 was projecting #2).
            const selfMatch = dogData.registeredName
              ? findAkcMatch(breedRankings, dogData.registeredName)
              : null;
            const selfId = selfMatch ? (selfMatch as any).id : null;
            const eligibleRows = selfId
              ? breedRankings.filter((r) => r.id !== selfId)
              : breedRankings;
            // Use max-rank-of-faster-dogs + 1 (not count + 1) so ties don't
            // double-count. AKC uses dense ranking (12, 12, 13), so the
            // slowest dog above you defines your rank label.
            const fasterRanks = eligibleRows
              .filter((r) => r.speed != null && r.speed > 0 && (r.speed as number) > rankingSpeed)
              .map((r) => r.rank);
            const rank = fasterRanks.length > 0 ? Math.max(...fasterRanks) + 1 : 1;
            const allRanks = breedRankings
              .filter((r) => r.speed != null && r.speed > 0)
              .map((r) => r.rank);
            const maxExistingRank = allRanks.length > 0 ? Math.max(...allRanks) : 0;
            setHypotheticalRank({ rank, total: maxExistingRank + 1 });
          } else {
            setHypotheticalRank(null);
          }
        } catch {
          setHypotheticalRank(null);
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to load dog");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }

    // Followers list — owner-only on the server side
    if (id) {
      try {
        const list = await getDogFollowers(id);
        setFollowers(list);
      } catch {
        setFollowers([]);
      }
    }
  }

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      fetchAll();
    }, [id])
  );

  function onRefresh() {
    setRefreshing(true);
    fetchAll();
  }

  async function handleDeleteRun(runId: string) {
    try {
      await apiDeleteRun(runId);
      fetchAll(); // Refresh to recalculate cumulatives
    } catch (err: any) {
      setError(err.message || "Failed to delete run");
    }
  }

  function handleEditRun(run: Run) {
    router.push(`/dogs/${id}/log-run?editId=${run.id}&editDate=${run.date.split("T")[0]}&editTime=${run.timeSeconds}&editRunNumber=${run.runNumber || 1}&editNotes=${encodeURIComponent(run.notes || "")}&editLocation=${encodeURIComponent((run as any).location || "")}&editEventName=${encodeURIComponent((run as any).eventName || "")}&editLureOperator=${encodeURIComponent((run as any).lureOperator || "")}` as any);
  }

  async function handlePickPhoto() {
    try {
      if (Platform.OS === "web") {
        // Web: use native file input + canvas resize
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = async (e: any) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const base64Uri = await resizeImageWeb(file, 300);
          try {
            await updateDog(id!, { photo: base64Uri } as any);
            setDog((prev) => prev ? { ...prev, photo: base64Uri } : prev);
          } catch (err: any) {
            setError(err.message || "Failed to upload photo");
          }
        };
        input.click();
      } else {
        // Native: use expo-image-picker
        const result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ["images"],
          allowsEditing: true,
          aspect: [1, 1],
          quality: 0.3,
          base64: true,
        });
        if (!result.canceled && result.assets[0]?.base64) {
          const base64Uri = `data:image/jpeg;base64,${result.assets[0].base64}`;
          try {
            await updateDog(id!, { photo: base64Uri } as any);
            setDog((prev) => prev ? { ...prev, photo: base64Uri } : prev);
          } catch (err: any) {
            setError(err.message || "Failed to upload photo");
          }
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to pick photo");
    }
  }

  // Resize image on web using canvas
  function resizeImageWeb(file: File, maxSize: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width;
          let h = img.height;
          if (w > h) { h = (h / w) * maxSize; w = maxSize; }
          else { w = (w / h) * maxSize; h = maxSize; }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.7));
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  if (loading && !refreshing) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8", justifyContent: "center", alignItems: "center" }}>
        <Text style={{ color: "#6b7280" }}>Loading...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      {/* Navigation header */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 14,
          backgroundColor: "#ffffff",
          borderBottomWidth: 1,
          borderBottomColor: "#e5e7eb",
        }}
      >
        {/* Always go to the dashboard home, never router.back(): dog profiles can
            stack up (Luigi → Mario), and back() would pop to the previous DOG
            instead of home. Deterministic home avoids the "back lands on the
            wrong dog" bug. */}
        <TouchableOpacity onPress={() => router.replace("/(tabs)" as any)} style={{ marginRight: 12 }}>
          <Ionicons name="arrow-back" size={22} color="#1B2A4A" />
        </TouchableOpacity>
        <Text style={{ fontSize: 18, fontWeight: "700", color: "#1B2A4A", flex: 1 }}>
          {dog?.name ?? "Dog Profile"}
        </Text>
        <TouchableOpacity onPress={() => router.push(`/dogs/${id}/edit` as any)} style={{ padding: 4 }}>
          <Ionicons name="pencil" size={20} color="#1B2A4A" />
        </TouchableOpacity>
      </View>

      <ScrollView
        contentContainerStyle={{ padding: 20, paddingBottom: 60 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {error ? (
          <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 12 }}>
            <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
          </View>
        ) : null}

        {/* Dog header card */}
        {dog && (
          <View
            style={{
              backgroundColor: "#1B2A4A",
              borderRadius: 16,
              padding: 20,
              marginBottom: 20,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              {/* Dog photo — tappable to upload */}
              <TouchableOpacity onPress={handlePickPhoto}>
                {dog.photo ? (
                  Platform.OS === "web" ? (
                    // @ts-ignore — respect owner-set photoFocusY for off-center photos
                    <img
                      src={dog.photo}
                      alt={dog.name}
                      style={{
                        width: 68, height: 68, borderRadius: 34,
                        border: "2px solid rgba(255,255,255,0.4)",
                        marginRight: 14,
                        objectFit: "cover",
                        objectPosition: `center ${dog.photoFocusY ?? 50}%`,
                      } as any}
                    />
                  ) : (
                    <Image
                      source={{ uri: dog.photo }}
                      style={{
                        width: 68,
                        height: 68,
                        borderRadius: 34,
                        borderWidth: 2,
                        borderColor: "rgba(255,255,255,0.4)",
                        marginRight: 14,
                      }}
                    />
                  )
                ) : (
                  <View
                    style={{
                      width: 68,
                      height: 68,
                      borderRadius: 34,
                      backgroundColor: "rgba(255,255,255,0.2)",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 14,
                      borderWidth: 2,
                      borderColor: "rgba(255,255,255,0.15)",
                      borderStyle: "dashed",
                    }}
                  >
                    <Ionicons name="camera" size={24} color="rgba(255,255,255,0.6)" />
                    <Text style={{ fontSize: 8, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>Add photo</Text>
                  </View>
                )}
              </TouchableOpacity>

              {/* Dog info */}
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 22, fontWeight: "800", color: "#ffffff" }}>{dog.name}</Text>
                {dog.registeredName && (
                  <Text style={{ fontSize: 14, color: "#94A3B8", marginTop: 2, fontStyle: "italic" }}>"{dog.registeredName}"</Text>
                )}
                <Text style={{ fontSize: 14, color: "#94A3B8", marginTop: 3 }}>{dog.breed}</Text>
                {(dog.handicap || dog.akcRegistrationNumber) && (
                  <Text style={{ fontSize: 15, color: "#94A3B8", marginTop: 2 }}>
                    {dog.handicap ? `${parseFloat(dog.handicap).toFixed(1)}× handicap` : ""}
                    {dog.handicap && dog.akcRegistrationNumber ? " · " : ""}
                    {dog.akcRegistrationNumber ? dog.akcRegistrationNumber : ""}
                  </Text>
                )}
              </View>

              {/* Ranking badges — side by side */}
              {stats && (() => {
                const akcMatch = findAkcMatch(rankings, dog.registeredName);
                return (
                <View style={{ alignItems: "center", marginLeft: 10, gap: 8 }}>
                  {/* Official AKC Rank */}
                  <View style={{ alignItems: "center" }}>
                    <Text style={{ fontSize: 10, color: "#94A3B8", marginBottom: 3, textTransform: "uppercase", fontWeight: "700", letterSpacing: 0.5 }}>Rank</Text>
                    <View
                      style={{
                        backgroundColor: akcMatch ? "#fbbf24" : "#4b5563",
                        borderRadius: 10,
                        paddingHorizontal: 14,
                        paddingVertical: 8,
                        alignItems: "center",
                        minWidth: 56,
                      }}
                    >
                      <Text style={{ fontSize: 20, fontWeight: "900", color: akcMatch ? "#78350f" : "#ffffff" }}>
                        {akcMatch ? `#${akcMatch.rank}` : "N/R"}
                      </Text>
                    </View>
                  </View>
                  {/* Projected Rank */}
                  {hypotheticalRank && (
                    <View style={{ alignItems: "center" }}>
                      <Text style={{ fontSize: 10, color: "#94A3B8", marginBottom: 3, textTransform: "uppercase", fontWeight: "700", letterSpacing: 0.5 }}>Projected</Text>
                      <View
                        style={{
                          backgroundColor: "#fbbf24",
                          borderRadius: 10,
                          paddingHorizontal: 14,
                          paddingVertical: 8,
                          alignItems: "center",
                          minWidth: 56,
                        }}
                      >
                        <Text style={{ fontSize: 20, fontWeight: "900", color: "#78350f" }}>
                          #{hypotheticalRank.rank}
                        </Text>
                      </View>
                      <Text style={{ fontSize: 10, color: "#94A3B8", marginTop: 3 }}>
                        of {hypotheticalRank.total}
                      </Text>
                    </View>
                  )}
                </View>
                );
              })()}
            </View>
          </View>
        )}

        {/* Share / Public Profile */}
        {dog && (
          <View style={{ flexDirection: "row", gap: 10, marginBottom: 20 }}>
            <TouchableOpacity
              onPress={() => {
                const currentlyPublic = !!(dog as any).isPublic;
                const makePublic = !currentlyPublic;
                const applyToggle = async () => {
                  try {
                    await updateDog(id!, { isPublic: makePublic } as any);
                    setDog((prev) => prev ? { ...prev, isPublic: makePublic } as any : prev);
                    if (makePublic) {
                      const slug = (dog.registeredName || dog.name).toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-");
                      const url = `https://dashhound.app/dog/${slug}`;
                      if (Platform.OS === "web") {
                        if (typeof navigator !== "undefined" && navigator.clipboard) {
                          await navigator.clipboard.writeText(url);
                        }
                        alert(`Profile is now public! Link copied:\n${url}`);
                      } else {
                        Alert.alert("Profile is Public!", `Share this link:\n${url}`);
                      }
                    }
                  } catch {}
                };
                // Confirm before making a PUBLIC profile private (easy to tap by accident);
                // making public stays one-tap.
                if (currentlyPublic) {
                  const msg = `This makes ${dog.name}'s profile private. The shareable link will stop working, and anyone following ${dog.name} won't be able to see the profile until you make it public again.`;
                  if (Platform.OS === "web") {
                    if (typeof window !== "undefined" && window.confirm(`Make ${dog.name}'s profile private?\n\n${msg}`)) applyToggle();
                  } else {
                    Alert.alert("Make profile private?", msg, [
                      { text: "Cancel", style: "cancel" },
                      { text: "Make Private", style: "destructive", onPress: applyToggle },
                    ]);
                  }
                } else {
                  applyToggle();
                }
              }}
              style={{
                flex: 1,
                backgroundColor: (dog as any).isPublic ? "#f0fdf4" : "#fff",
                borderRadius: 12,
                padding: 12,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                borderWidth: 1,
                borderColor: (dog as any).isPublic ? "#16a34a" : "#d1d5db",
              }}
            >
              <Ionicons name={(dog as any).isPublic ? "globe" : "globe-outline"} size={18} color={(dog as any).isPublic ? "#16a34a" : "#6b7280"} />
              <Text style={{ fontSize: 14, fontWeight: "600", color: (dog as any).isPublic ? "#16a34a" : "#6b7280" }}>
                {(dog as any).isPublic ? "Public — Tap to Make Private" : "Make Public"}
              </Text>
            </TouchableOpacity>
            {(dog as any).isPublic && (
              <TouchableOpacity
                onPress={async () => {
                  // Use the actual stored publicSlug (fallback to dog id) so the URL
                  // matches what the server expects, not a re-derived guess.
                  const slug = (dog as any).publicSlug || dog.id;
                  const url = `https://dashhound.app/dog/${slug}`;
                  if (Platform.OS === "web") {
                    const shareData = {
                      title: `${dog.name}'s Fast CAT profile`,
                      text: `Check out ${dog.name}'s Fast CAT profile on Dash-Hound!`,
                      url,
                    };
                    if (typeof navigator !== "undefined" && (navigator as any).share) {
                      try {
                        await (navigator as any).share(shareData);
                      } catch {}
                    } else if (typeof navigator !== "undefined" && navigator.clipboard) {
                      await navigator.clipboard.writeText(url);
                      alert(`Link copied!\n${url}`);
                    } else {
                      alert(`Share this link:\n${url}`);
                    }
                  } else {
                    // Android: Share.share `url` is iOS-only — drop the URL into
                    // the message itself so it actually goes through.
                    const message = Platform.OS === "android"
                      ? `Check out ${dog.name}'s Fast CAT profile on Dash-Hound! ${url}`
                      : `Check out ${dog.name}'s Fast CAT profile on Dash-Hound!`;
                    await Share.share({
                      message,
                      ...(Platform.OS === "ios" ? { url } : {}),
                    });
                  }
                }}
                style={{
                  backgroundColor: "#1B2A4A",
                  borderRadius: 12,
                  padding: 12,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  paddingHorizontal: 20,
                }}
              >
                <Ionicons name="share-outline" size={18} color="#fff" />
                <Text style={{ fontSize: 14, fontWeight: "600", color: "#fff" }}>Share</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Followers nudge — only shows on public dogs. Tap to open the
            dedicated Followers page. */}
        {(dog as any)?.isPublic && followers !== null && (
          <TouchableOpacity
            onPress={() =>
              router.push(`/(tabs)/dogs/followers?dogId=${id}&dogName=${encodeURIComponent(dog?.name || "")}` as any)
            }
            style={{
              backgroundColor: "#fff",
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "#e5e7eb",
              paddingVertical: 12,
              paddingHorizontal: 14,
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              marginBottom: 20,
            }}
          >
            <View style={{ width: 30, height: 30, borderRadius: 15, backgroundColor: "#FEF3C7", alignItems: "center", justifyContent: "center" }}>
              <Ionicons name="people" size={16} color="#F5A623" />
            </View>
            <Text style={{ flex: 1, fontSize: 14, color: "#374151" }}>
              {followers.length === 0 ? "No followers yet" : (
                <Text>
                  Followed by <Text style={{ fontWeight: "700" }}>{followers.length}</Text> {followers.length === 1 ? "handler" : "handlers"}
                </Text>
              )}
            </Text>
            {followers.length > 0 && <Ionicons name="chevron-forward" size={16} color="#9ca3af" />}
          </TouchableOpacity>
        )}

        {/* Log Run button */}
        <TouchableOpacity
          onPress={() => router.push(`/dogs/${id}/log-run` as any)}
          style={{
            backgroundColor: "#1B2A4A",
            borderRadius: 12,
            padding: 16,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 20,
            gap: 8,
          }}
        >
          <Ionicons name="add-circle-outline" size={22} color="#ffffff" />
          <Text style={{ color: "#ffffff", fontSize: 16, fontWeight: "700" }}>Log a Run</Text>
        </TouchableOpacity>

        {/* Import buttons — side by side */}
        <View style={{ flexDirection: "row", gap: 10, marginBottom: 20 }}>
          <TouchableOpacity
            onPress={() => router.push(`/dogs/${id}/import-runs` as any)}
            style={{
              flex: 1,
              backgroundColor: "#ffffff",
              borderRadius: 12,
              paddingVertical: 14,
              paddingHorizontal: 8,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              borderWidth: 1,
              borderColor: "#d1d5db",
            }}
          >
            <Ionicons name="cloud-upload-outline" size={18} color="#1B2A4A" />
            <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.85} style={{ color: "#1B2A4A", fontSize: 14, fontWeight: "600", flexShrink: 1 }}>Import CSV</Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push(`/dogs/${id}/import-screenshot` as any)}
            style={{
              flex: 1,
              backgroundColor: "#ffffff",
              borderRadius: 12,
              paddingVertical: 14,
              paddingHorizontal: 8,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              borderWidth: 1,
              borderColor: "#d1d5db",
            }}
          >
            <Ionicons name="camera-outline" size={18} color="#1B2A4A" />
            <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.85} style={{ color: "#1B2A4A", fontSize: 14, fontWeight: "600", flexShrink: 1 }}>Import Screenshot</Text>
          </TouchableOpacity>
        </View>

        {/* Share Card button — fully hidden everywhere while we iterate on the
            trading card UX. Flip the false → true (or restore the dog-id gate)
            to bring it back. */}
        {false && dog && id === "bdca961c-1647-4180-9e47-1e9ad55d4cc1" && Platform.OS !== "web" && (
          <TouchableOpacity
            onPress={() => router.push(`/dogs/${id}/card` as any)}
            style={{
              flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 8,
              backgroundColor: "#1B2A4A",
              paddingVertical: 13, paddingHorizontal: 18,
              borderRadius: 12, marginBottom: 16,
            }}
          >
            <Ionicons name="card-outline" size={18} color="#F5A623" />
            <Text style={{ color: "#fff", fontSize: 15, fontWeight: "700" }}>View Trading Card</Text>
          </TouchableOpacity>
        )}

        {/* Performance stats — top card behavior:
              • Has current-year runs → "2026 Performance Stats" (year-scoped)
                + Total Points (all-time) + Career Performance block (if not rookie)
              • Has runs but NONE in current year → fall back to plain "Performance
                Stats" with lifetime numbers (no Career block — the card IS career)
              • Has 0 runs → use lifetime (which will show 0s + historical points) */}
        {stats && (() => {
          const currentYear = new Date().getFullYear();
          const yearStats = computeYearStats(runs, currentYear);
          const hasCurrentYearRuns = yearStats.totalRuns > 0;
          if (!hasCurrentYearRuns) {
            return <StatsCard stats={stats} />;
          }
          const isRookie = runs.every((r) => new Date(r.date).getFullYear() === currentYear);
          const stub: any = {
            ...stats,
            totalRuns: yearStats.totalRuns,
            personalBestTime: yearStats.personalBestTime,
            personalBestSpeed: yearStats.personalBestSpeed,
            totalPoints: yearStats.totalPoints,
            bestThreeAvgSpeed: yearStats.bestThreeAvgSpeed,
            run1AvgSpeed: yearStats.run1AvgSpeed,
            run2AvgSpeed: yearStats.run2AvgSpeed,
            run1Count: yearStats.run1Count,
            run2Count: yearStats.run2Count,
          };
          return (
            <StatsCard
              stats={stub}
              yearLabel={String(currentYear)}
              totalPointsAllTime={stats.totalPoints}
              careerStats={isRookie ? undefined : {
                totalRuns: stats.totalRuns,
                personalBestTime: stats.personalBestTime,
                personalBestSpeed: stats.personalBestSpeed,
              }}
            />
          );
        })()}

        {/* Titles earned */}
        {stats && <TitlesEarned stats={stats} />}

        {/* Title progress + pace-to-next-title projection */}
        {stats && (() => {
          // Use last 5 runs for pace projection, fall back to all-time if fewer than 5
          const runsByDateDesc = [...runs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          const sample = runsByDateDesc.length >= 5 ? runsByDateDesc.slice(0, 5) : runsByDateDesc;
          const avgPointsPerRun = sample.length > 0
            ? sample.reduce((sum, r) => sum + (parseFloat(r.points) || 0), 0) / sample.length
            : 0;
          return <TitleProgress stats={stats} avgPointsPerRun={avgPointsPerRun} runsLogged={runs.length} />;
        })()}

        {/* Speed over time chart */}
        {runs.length >= 2 && <SpeedChart runs={runs} />}

        {/* Dash Insight — personalized rotating insight (between Speed Over Time and Rankings) */}
        {dog && (
          <DashInsight
            dog={dog}
            runs={runs}
            stats={stats}
            rankings={rankings}
            hypotheticalRank={hypotheticalRank}
          />
        )}

        {/* Breed standings */}
        {dog && rankings.length > 0 && (
          <BreedStandings
            rankings={rankings}
            dog={dog}
            stats={stats}
            hypotheticalRank={hypotheticalRank}
          />
        )}

        {/* Dash-Fact — top 3 average run time vs your dog */}
        {dog && rankings.length > 0 && (
          <DashFact
            rankings={rankings}
            breed={dog.breed}
            userBestThreeAvgSpeed={stats?.bestThreeAvgSpeed ?? null}
          />
        )}

        {/* Historical AKC rankings — accordion of past years (only shows years where dog was ranked) */}
        {dog && (
          <HistoricalRankings
            dogId={dog.id}
            hasRegisteredName={!!(dog.registeredName && dog.registeredName.trim())}
            currentYear={new Date().getFullYear().toString()}
            onAddRegisteredName={() => router.push(`/dogs/${dog.id}/edit` as any)}
          />
        )}

        {/* Run history */}
        {(() => {
          const yearsSet = new Set<string>();
          runs.forEach((r) => yearsSet.add(new Date(r.date).getFullYear().toString()));
          const years = Array.from(yearsSet).sort((a, b) => Number(b) - Number(a));
          const yearFiltered = runYearFilter === "all" ? runs : runs.filter((r) => new Date(r.date).getFullYear().toString() === runYearFilter);
          const filtered = [...yearFiltered].sort((a, b) => {
            if (runSort === "fastest") return parseFloat(b.speedMph as any) - parseFloat(a.speedMph as any);
            if (runSort === "slowest") return parseFloat(a.speedMph as any) - parseFloat(b.speedMph as any);
            // "newest" — date desc, then created_at as tie-breaker
            const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
            if (dateDiff !== 0) return dateDiff;
            return ((b as any).createdAt ? new Date((b as any).createdAt).getTime() : 0) - ((a as any).createdAt ? new Date((a as any).createdAt).getTime() : 0);
          });
          const showPicker = years.length >= 2;
          const headerCount = runs.length === 0
            ? ""
            : showPicker && runYearFilter !== "all"
              ? ` (${filtered.length} of ${runs.length})`
              : ` (${runs.length})`;

          return (
            <>
              <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A" }}>
                  Run History{headerCount}
                </Text>
                {showPicker && (
                  <TouchableOpacity
                    onPress={() => setRunYearPickerOpen(true)}
                    style={{ flexDirection: "row", alignItems: "center", gap: 6, backgroundColor: "#ffffff", borderRadius: 8, borderWidth: 1, borderColor: "#e5e7eb", paddingHorizontal: 12, paddingVertical: 7 }}
                  >
                    <Text style={{ fontSize: 13, fontWeight: "700", color: "#1B2A4A" }}>{runYearFilter === "all" ? "All years" : runYearFilter}</Text>
                    <Ionicons name="chevron-down" size={14} color="#6b7280" />
                  </TouchableOpacity>
                )}
              </View>

              {runs.length > 0 && (
                <View style={{ flexDirection: "row", gap: 6, marginBottom: 12 }}>
                  {([
                    { key: "newest", label: "Newest", icon: "time-outline" },
                    { key: "fastest", label: "Fastest", icon: "flash-outline" },
                    { key: "slowest", label: "Slowest", icon: "trending-down-outline" },
                  ] as const).map((opt) => {
                    const selected = runSort === opt.key;
                    return (
                      <TouchableOpacity
                        key={opt.key}
                        onPress={() => setRunSort(opt.key)}
                        style={{
                          flex: 1,
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          paddingVertical: 8,
                          paddingHorizontal: 6,
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor: selected ? "#1B2A4A" : "#e5e7eb",
                          backgroundColor: selected ? "#1B2A4A" : "#ffffff",
                        }}
                      >
                        <Ionicons name={opt.icon as any} size={14} color={selected ? "#ffffff" : "#6b7280"} />
                        <Text style={{ fontSize: 12, fontWeight: "700", color: selected ? "#ffffff" : "#374151" }}>{opt.label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}

              {runs.length === 0 ? (
                <View
                  style={{
                    alignItems: "center",
                    paddingVertical: 32,
                    backgroundColor: "#ffffff",
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: "#e5e7eb",
                  }}
                >
                  <Ionicons name="time-outline" size={40} color="#d1d5db" />
                  <Text style={{ color: "#6b7280", marginTop: 12, fontSize: 14 }}>
                    No runs logged yet
                  </Text>
                </View>
              ) : filtered.length === 0 ? (
                <View style={{ alignItems: "center", paddingVertical: 24, backgroundColor: "#ffffff", borderRadius: 12, borderWidth: 1, borderColor: "#e5e7eb" }}>
                  <Text style={{ color: "#6b7280", fontSize: 14 }}>No runs in {runYearFilter}</Text>
                </View>
              ) : (() => {
                // Find the all-time PB run (highest speed across ALL runs, not just the filtered view)
                // so the PB badge stays attached to the same run regardless of year filter.
                const pbRunId = runs.length > 0
                  ? runs.reduce((best, r) => parseFloat(r.speedMph as any) > parseFloat(best.speedMph as any) ? r : best).id
                  : null;
                return filtered.map((run) => (
                  <RunCard
                    key={run.id}
                    run={run}
                    isPersonalBest={run.id === pbRunId}
                    onEdit={handleEditRun}
                    onDelete={handleDeleteRun}
                    showAdvanced
                  />
                ));
              })()}

              {/* Year picker modal */}
              <Modal visible={runYearPickerOpen} transparent animationType="fade" onRequestClose={() => setRunYearPickerOpen(false)}>
                <Pressable
                  onPress={() => setRunYearPickerOpen(false)}
                  style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.4)", justifyContent: "center", alignItems: "center", padding: 24 }}
                >
                  <Pressable onPress={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 320, backgroundColor: "#fff", borderRadius: 14, overflow: "hidden" }}>
                    <Text style={{ fontSize: 14, fontWeight: "700", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, paddingHorizontal: 16, paddingTop: 16, paddingBottom: 8 }}>
                      Filter by year
                    </Text>
                    {[ "all", ...years ].map((y) => {
                      const selected = runYearFilter === y;
                      return (
                        <TouchableOpacity
                          key={y}
                          onPress={() => { setRunYearFilter(y); setRunYearPickerOpen(false); }}
                          style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 16, paddingVertical: 14, borderTopWidth: 1, borderTopColor: "#f3f4f6" }}
                        >
                          <Text style={{ fontSize: 15, fontWeight: selected ? "700" : "500", color: selected ? "#1B2A4A" : "#374151" }}>
                            {y === "all" ? "All years" : y}
                          </Text>
                          {selected && <Ionicons name="checkmark" size={20} color="#1B2A4A" />}
                        </TouchableOpacity>
                      );
                    })}
                  </Pressable>
                </Pressable>
              </Modal>
            </>
          );
        })()}
      </ScrollView>
    </SafeAreaView>
  );
}
